import React from 'react';
import {
  Box,
  Typography,
  Divider,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import AddressDialog from './AddressDialog';

const ReviewSection = ({
  formData,
  panDetails,
  gstDetails,
  tanDetails,
  chequeDetails,
  bankDetails,
  aadhaarDetails,
  altDocType,
  incFileLink,
  partnershipFileLink,
  passbookFileLink,
  parseAddress,
  additionalAddress,
  checked,
  onCheckboxChange,
  open,
  onClose,
  onInputChange
}) => {
  const { statusOfCustomer, gstStatus } = formData;
  const isNormalRegistered = gstStatus === 'Normal Registered';
  const isForeignEntity = statusOfCustomer === 'Foreign Entity';

  const renderPanDetails = () => (
    <>
      <Typography variant="h6" color="secondary">PAN Details</Typography>
      <Divider />
      <Typography><strong>Name:</strong> {panDetails?.name || '-'}</Typography>
      <Typography><strong>PAN Number:</strong> {panDetails?.panNumber || '-'}</Typography>
      <Typography><strong>Date:</strong> {panDetails?.dob || panDetails?.dateOfIncorporation || '-'}</Typography>
    </>
  );

  const renderGstDetails = () => {
    if (!isNormalRegistered || isForeignEntity) return null;

    const { city, district, state, country, pin } = parseAddress(gstDetails?.principalPlaceAddress || '');

    return (
      <>
        <Typography variant="h6" color="secondary" sx={{ mt: 2 }}>
          GST Details
        </Typography>
        <Divider />
        <Typography><strong>Legal Name:</strong> {gstDetails?.legalName || '-'}</Typography>
        <Typography><strong>Trade Name:</strong> {gstDetails?.tradeName || '-'}</Typography>
        <Typography><strong>Registration #:</strong> {gstDetails?.registrationNumber || '-'}</Typography>
        <Typography><strong>Valid From:</strong> {gstDetails?.validityFrom || '-'}</Typography>
        <Typography><strong>Valid To:</strong> {gstDetails?.validityTo || '-'}</Typography>
        <Typography><strong>Address:</strong> {gstDetails?.principalPlaceAddress || '-'}</Typography>
        {gstDetails?.principalPlaceAddress && (
          <>
            <Typography><strong>City:</strong> {city || '-'}</Typography>
            <Typography><strong>District:</strong> {district || '-'}</Typography>
            <Typography><strong>State:</strong> {state || '-'}</Typography>
            <Typography><strong>Country:</strong> {country || '-'}</Typography>
            <Typography><strong>Pin Code:</strong> {pin || '-'}</Typography>
          </>
        )}
        <FormControlLabel
          control={
            <Checkbox
              checked={checked}
              onChange={onCheckboxChange}
              color="primary"
            />
          }
          label="Invoice to a different address than the one on the GST certificate"
          sx={{
            mt: 1,
            '& .MuiFormControlLabel-label': {
              color: 'red',
              fontWeight: 'bold',
              fontSize: '0.9rem',
              fontStyle: 'italic',
            }
          }}
        />
        <AddressDialog
          open={open}
          onClose={onClose}
          additionalAddress={additionalAddress}
          onInputChange={onInputChange}
        />
        {additionalAddress?.address && (
          <>
            <Typography variant="h6" color="secondary" sx={{ mt: 2 }}>
              Additional Business Address
            </Typography>
            <Divider />
            <Typography><strong>Address:</strong> {additionalAddress.address || '-'}</Typography>
            <Typography><strong>City:</strong> {additionalAddress.city || '-'}</Typography>
            <Typography><strong>District:</strong> {additionalAddress.district || '-'}</Typography>
            <Typography><strong>State:</strong> {additionalAddress.state || '-'}</Typography>
            <Typography><strong>Pin Code:</strong> {additionalAddress.pincode || '-'}</Typography>
          </>
        )}
      </>
    );
  };

  const renderAddressProof = () => {
    if (isNormalRegistered && !isForeignEntity) return null;

    return (
      <>
        <Typography variant="h6" color="secondary" sx={{ mt: 2 }}>
          Address Proof
        </Typography>
        <Divider />
        <Typography>
          <strong>Type of Document:</strong> {altDocType ? (altDocType === 'passport' ? 'Passport' : altDocType === 'dl' ? "Driver's License" : 'Voter ID') : '-'}
        </Typography>
        {!altDocType && (
          <Typography>
            <strong>Address:</strong> {aadhaarDetails?.address || '-'}
          </Typography>
        )}
      </>
    );
  };

  const renderTanDetails = () => {
    if (!tanDetails?.tanNumber) return null;

    return (
      <>
        <Typography variant="h6" color="secondary" sx={{ mt: 2 }}>
          TAN Details
        </Typography>
        <Divider />
        <Typography><strong>TAN Number:</strong> {tanDetails.tanNumber}</Typography>
      </>
    );
  };

  const renderIncorporationCertificate = () => {
    const showForCustomers = [
      'Private Limited/ Public Limited',
      'LLP',
      'Registered Partnership',
      'Foreign Entity'
    ];
    
    if (!showForCustomers.includes(statusOfCustomer) || !incFileLink) return null;

    return (
      <>
        <Typography variant="h6" color="secondary" sx={{ mt: 2 }}>
          Certificate of Incorporation
        </Typography>
        <Divider />
        <Typography>
          <a href={incFileLink} target="_blank" rel="noopener noreferrer">
            View document
          </a>
        </Typography>
      </>
    );
  };

  const renderPartnershipDeed = () => {
    if (statusOfCustomer !== 'Registered Partnership' || !partnershipFileLink) return null;

    return (
      <>
        <Typography variant="h6" color="secondary" sx={{ mt: 2 }}>
          Partnership Deed
        </Typography>
        <Divider />
        <Typography>
          <a href={partnershipFileLink} target="_blank" rel="noopener noreferrer">
            View document
          </a>
        </Typography>
      </>
    );
  };

  const renderPassbookFrontPage = () => {
    const showForCustomers = [
      'Private Limited/ Public Limited',
      'LLP',
      'Unregistered Partnership',
      'Society/AOP/HUF/Trust',
    ];
    
    if (!showForCustomers.includes(statusOfCustomer) || gstStatus !== 'Un-Registered' || !passbookFileLink) return null;

    return (
      <>
        <Typography variant="h6" color="secondary" sx={{ mt: 2 }}>
          Passbook Front Page
        </Typography>
        <Divider />
        <Typography>
          <a href={passbookFileLink} target="_blank" rel="noopener noreferrer">
            View document
          </a>
        </Typography>
      </>
    );
  };

  const renderChequeDetails = () => {
    if (isForeignEntity) return null;

    return (
      <>
        <Typography variant="h6" color="secondary" sx={{ mt: 2 }}>
          Cancelled Cheque
        </Typography>
        <Divider />
        <Typography><strong>Bank Name:</strong> {chequeDetails.bankName || bankDetails.bankName || '-'}</Typography>
        <Typography><strong>IFSC:</strong> {chequeDetails.ifsc || bankDetails.ifsc || '-'}</Typography>
        <Typography><strong>Account #:</strong> {chequeDetails.accountNumber || bankDetails.accountNumber || '-'}</Typography>
        <Typography><strong>Cheque #:</strong> {chequeDetails.chequeNumber || bankDetails.chequeNumber || '-'}</Typography>
      </>
    );
  };

  const renderTypeOfDocument = () => {
    if (gstStatus === 'Normal Registered' || statusOfCustomer !== 'Proprietor / Individual') return null;

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6" color="secondary">Type of Document</Typography>
        <Divider />
        <Typography>
          <strong>Type of Document:</strong> {altDocType ? (altDocType === 'passport' ? 'Passport' : altDocType === 'dl' ? "Driver's License" : 'Voter ID') : '-'}
        </Typography>
        {!altDocType && (
          <Typography>
            <strong>Address:</strong> {aadhaarDetails?.address || '-'}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {renderPanDetails()}
      {renderGstDetails()}
      {renderAddressProof()}
      {renderTanDetails()}
      {renderIncorporationCertificate()}
      {renderPartnershipDeed()}
      {renderPassbookFrontPage()}
      {renderChequeDetails()}
      {renderTypeOfDocument()}
    </Box>
  );
};

export default ReviewSection;